import { Market, MarketCategory, MarketType, Option } from '@/feature/event-details-sbk/types';
import { TeamOrder } from '@/utils/sport-utils';

export type MarketColumn = {
    header: string;
    marketType?: MarketType;
    markets: Market[];
};

export type MarketColumnType = 'spread' | 'total' | 'winner';

// overrideCode: market that replaces the base market on the card when present
// (e.g. soccer RESULT_EXTRA_TIME overrides RESULT once the game reaches extra time)
type MarketDef = { code: string; selections: number; overrideCode?: string };

type SportMarketConfig = Partial<Record<MarketColumnType, MarketDef>>;

// Single source of truth for sport-specific market configuration.
// Each sport defines which columns it uses and the market code + selection count for each.
// Sports not listed here use the default config.
const defaultConfig: SportMarketConfig = {
    spread: { code: 'POINT_HANDICAP', selections: 2 },
    total: { code: 'POINT_OVER_UNDER', selections: 2 },
    winner: { code: 'RESULT', selections: 2 },
};

const WINNER_ONLY_CONFIG: SportMarketConfig = {
    winner: { code: 'RESULT', selections: 2 },
};

const sportMarketConfig: Record<string, SportMarketConfig> = {
    Soccer: {
        winner: { code: 'RESULT', selections: 3, overrideCode: 'RESULT_EXTRA_TIME' },
    },
    Tennis: WINNER_ONLY_CONFIG,
    Baseball: {
        spread: { code: 'RUN_HANDICAP', selections: 2 },
        total: { code: 'RUN_OVER_UNDER', selections: 2 },
        winner: { code: 'RESULT', selections: 2 },
    },
    MMA: WINNER_ONLY_CONFIG,
    Boxing: WINNER_ONLY_CONFIG,
    'Ice Hockey': {
        winner: { code: 'RESULT', selections: 2 },
        total: { code: 'GOAL_OVER_UNDER', selections: 2 },
        spread: { code: 'GOAL_HANDICAP', selections: 2 },
    },
};

const getSportConfig = (sportName?: string): SportMarketConfig => {
    if (sportName && sportName in sportMarketConfig) {
        return sportMarketConfig[sportName];
    }
    return defaultConfig;
};

// Defines the display order of market columns
const columnOrder: MarketColumnType[] = ['spread', 'total', 'winner'];

// Returns the column types available for a sport (e.g. Soccer -> ['winner'])
export const getMarketColumnTypes = (sportName: string): MarketColumnType[] => {
    const config = getSportConfig(sportName);
    return columnOrder.filter(type => type in config);
};

// Returns all popular market definitions for a sport
const getPopularMarketDefs = (sportName?: string): MarketDef[] => {
    const config = getSportConfig(sportName);
    return Object.values(config);
};

const matchesMarketDef = (code: string, selections: number, defs: MarketDef[]) => {
    return defs.some(def => (def.code === code || def.overrideCode === code) && def.selections === selections);
};

// Filter markets to display on the event matchup card
export const filterPopularMarkets = (marketCategory: MarketCategory, sportName?: string) => {
    const { code, params } = marketCategory.market_type;
    return matchesMarketDef(code, params?.selections ?? 0, getPopularMarketDefs(sportName));
};

export const isMatchupCardWinner = (marketCategory: MarketCategory, sportName?: string) => {
    const winnerDef = getSportConfig(sportName).winner;
    if (!winnerDef) return false;
    const { code, params } = marketCategory.market_type;
    return matchesMarketDef(code, params.selections ?? 0, [winnerDef]);
};

// Returns the market category for the card's winner column, preferring the sport's
// override market (e.g. soccer RESULT_EXTRA_TIME) over the base one when both exist
export const findWinnerMarketCategory = (marketCategories: MarketCategory[], sportName?: string) => {
    const winnerDef = getSportConfig(sportName).winner;
    if (!winnerDef) return undefined;
    const overrideCategory = winnerDef.overrideCode
        ? marketCategories.find(cat => cat.market_type.code === winnerDef.overrideCode)
        : undefined;
    return overrideCategory ?? marketCategories.find(cat => cat.market_type.code === winnerDef.code);
};

export const isPopularMarket = (marketType: MarketType, sportName?: string) => {
    return matchesMarketDef(marketType.code, marketType.params?.selections ?? 0, getPopularMarketDefs(sportName));
};

// Market type code groups used for sorting and column matching
export const totalsMarketTypes = ['POINT_OVER_UNDER', 'RUN_OVER_UNDER', 'GOAL_OVER_UNDER'];
export const spreadMarketTypes = ['POINT_HANDICAP', 'RUN_HANDICAP', 'GOAL_HANDICAP'];
export const resultMarketTypes = Array.from(
    new Set(
        [defaultConfig, ...Object.values(sportMarketConfig)]
            .flatMap(cfg => (cfg.winner ? [cfg.winner.code, cfg.winner.overrideCode] : []))
            .filter((c): c is string => Boolean(c))
    )
);

const totalsOptionOrder = ['OVER', 'UNDER'];

// Sorts the options in the market columns to match team row order
export const sortOptions = (a: Option, b: Option, marketType: MarketType, teamOrder: TeamOrder) => {
    if (totalsMarketTypes.includes(marketType.code)) {
        // sort by option.code with OVER first then UNDER
        return totalsOptionOrder.indexOf(a.option_type.code) - totalsOptionOrder.indexOf(b.option_type.code);
    }
    // Derive option order from teamOrder (e.g., ['away', 'home'] → ['AWAY', 'HOME'])
    const optionOrder = teamOrder.map(side => side.toUpperCase());
    if (resultMarketTypes.includes(marketType.code) && marketType.params?.selections === 3) {
        // 3-way result: first team, second team, then DRAW
        const threeWayOrder = [...optionOrder, 'DRAW'];
        return threeWayOrder.indexOf(a.option_type.code) - threeWayOrder.indexOf(b.option_type.code);
    }
    if (spreadMarketTypes.includes(marketType.code) || resultMarketTypes.includes(marketType.code)) {
        return optionOrder.indexOf(a.option_type.code) - optionOrder.indexOf(b.option_type.code);
    }
    return 0;
};

export const getSelectionCount = (marketColumns: { marketType?: MarketType }[]): number => {
    return Math.max(...marketColumns.map(col => col.marketType?.params?.selections ?? 2));
};

// Checks if any market column has a 3-way result (e.g., soccer with HOME/AWAY/DRAW)
export const hasThreeWayResult = (marketColumns: { marketType?: MarketType }[]): boolean => {
    return marketColumns.some(
        col => resultMarketTypes.includes(col.marketType?.code ?? '') && col.marketType?.params?.selections === 3
    );
};
